import { useState, useRef, useEffect } from 'react';
import CommentBody, { hasMedia } from './CommentBody';

const MAX_COMMENT_LENGTH = 2000;

function CharactersRemaining({ length }: { length: number }) {
  const remaining = MAX_COMMENT_LENGTH - length;

  const colorClass =
    remaining <= 50
      ? 'text-red-500 font-bold'
      : remaining <= 200
        ? 'text-amber-500 font-bold'
        : 'text-gray-600 font-bold';

  return (
    <span className={`ml-auto text-sm tabular-nums ${colorClass} inline-flex items-center gap-1`}>
      {remaining}
      <span className="text-xs opacity-60">left</span>
    </span>
  );
}

interface CommentFormProps {
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  initialValue?: string;
}

export default function CommentForm({
  onSubmit,
  onCancel,
  placeholder = 'Write a comment…',
  submitLabel = 'Submit',
  autoFocus = false,
  initialValue = '',
}: CommentFormProps) {
  const [body, setBody] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      setBody('');
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        maxLength={MAX_COMMENT_LENGTH}
        rows={3}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={isSubmitting}
      />
      {hasMedia(body) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="mb-1 text-xs text-gray-400">Preview</p>
          <CommentBody text={body} />
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isSubmitting || !body.trim()}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting…' : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        )}
        <CharactersRemaining length={body.length} />
      </div>
    </form>
  );
}
